import { useSyncExternalStore } from "react";
import { getStoredOrgRole } from "../../../utils/org-role";

// Support Bot — Knowledge / Approvals / My Submissions. No APIs yet (per
// direct ask): everything below is hardcoded sample data held in one small
// in-memory store, so an approve/upload on one screen is reflected on the
// others and on the sidebar's Approvals badge for the rest of the session.
// Swap the store's actions for real calls once the endpoints exist.

export type DocStatus = "live" | "pending" | "changes_requested" | "failed" | "rejected";
export type Audience = "Basic" | "Advanced" | "Family" | "Partner";

export const PRODUCTS = ["Product A", "Product B", "Product C"] as const;
export const DOC_TYPES = ["User guide", "API guide", "FAQ", "Troubleshooting", "Policy"] as const;
export const AUDIENCES: Audience[] = ["Basic", "Advanced", "Family", "Partner"];
export const OWNERS = ["Siddhi (CRM Admin)", "Rohit (Agent)", "Priya (Agent)"];

export interface HistoryEvent {
  at: string;
  by: string;
  text: string;
}

export interface KnowledgeDoc {
  id: string;
  name: string;
  product: string;
  type: string;
  visibleTo: Audience[];
  uploadedBy: string;
  status: DocStatus;
  updated: string;
  size?: string;
  note?: string;
  adminComment?: string;
  preview: string;
  history: HistoryEvent[];
  // True for anything the signed-in user uploaded — what My Submissions lists.
  mine?: boolean;
}

const PREVIEW =
  "Section 1 — Overview. This document explains how customers reset their password and reconnect their account. Step 1: open Settings → Security…";

let docs: KnowledgeDoc[] = [
  { id: "d1", name: "userGuide.pdf", product: "Product A", type: "User guide", visibleTo: ["Basic", "Advanced"], uploadedBy: "Siddhi (CRM Admin)", status: "live", updated: "2d ago", preview: PREVIEW, history: [{ at: "2d ago", by: "Siddhi (CRM Admin)", text: "Published to knowledge base" }] },
  { id: "d2", name: "userGuide.md", product: "Product A", type: "API guide", visibleTo: ["Partner"], uploadedBy: "Siddhi (CRM Admin)", status: "live", updated: "5d ago", preview: PREVIEW, mine: true, history: [{ at: "6d ago", by: "Priya (Agent)", text: "Submitted for approval" }, { at: "5d ago", by: "Siddhi (CRM Admin)", text: "Approved" }] },
  { id: "d3", name: "userGuide.docx", product: "Product B", type: "User guide", visibleTo: ["Basic"], uploadedBy: "Rohit (Agent)", status: "pending", updated: "1h ago", preview: PREVIEW, history: [{ at: "1h ago", by: "Rohit (Agent)", text: "Submitted for approval" }] },
  { id: "d4", name: "userGuide.pdf", product: "Product B", type: "User guide", visibleTo: ["Advanced", "Family"], uploadedBy: "Siddhi (CRM Admin)", status: "live", updated: "9d ago", preview: PREVIEW, history: [{ at: "9d ago", by: "Siddhi (CRM Admin)", text: "Published to knowledge base" }] },
  { id: "d5", name: "userGuide.docx", product: "Product C", type: "FAQ", visibleTo: ["Basic", "Advanced", "Family", "Partner"], uploadedBy: "Priya (Agent)", status: "changes_requested", updated: "3h ago", preview: PREVIEW, mine: true, adminComment: "Please fix the table formatting in section 2, then resubmit.", history: [{ at: "5h ago", by: "Priya (Agent)", text: "Submitted for approval" }, { at: "3h ago", by: "Siddhi (CRM Admin)", text: "Requested changes — fix the table formatting in section 2" }] },
  { id: "d6", name: "userGuide.pdf", product: "Product B", type: "Troubleshooting", visibleTo: ["Advanced"], uploadedBy: "Siddhi (CRM Admin)", status: "failed", updated: "1d ago", preview: PREVIEW, history: [{ at: "1d ago", by: "System", text: "Failed validation — no readable text found" }] },
  { id: "d7", name: "userGuide.docx", product: "Product C", type: "User guide", visibleTo: ["Basic"], uploadedBy: "Priya (Agent)", status: "pending", updated: "1d ago", preview: PREVIEW, mine: true, history: [{ at: "1d ago", by: "Priya (Agent)", text: "Submitted for approval" }] },
  { id: "d8", name: "userGuide.pdf", product: "Product A", type: "User guide", visibleTo: ["Basic"], uploadedBy: "Siddhi (CRM Admin)", status: "pending", updated: "2d ago", preview: PREVIEW, history: [{ at: "2d ago", by: "Siddhi (CRM Admin)", text: "Submitted for approval" }] },
  { id: "d9", name: "userGuide.pdf", product: "Product B", type: "Troubleshooting", visibleTo: ["Advanced"], uploadedBy: "Priya (Agent)", status: "rejected", updated: "4d ago", preview: PREVIEW, mine: true, adminComment: "Duplicates the existing troubleshooting guide.", history: [{ at: "5d ago", by: "Priya (Agent)", text: "Submitted for approval" }, { at: "4d ago", by: "Siddhi (CRM Admin)", text: "Rejected — duplicates the existing guide" }] },
];

// Documents the knowledge table lists (rejected ones never reached it).
export const isInKnowledge = (d: KnowledgeDoc) => d.status !== "rejected";

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useSupportBotDocs(): KnowledgeDoc[] {
  return useSyncExternalStore(subscribe, () => docs);
}

const update = (id: string, patch: Partial<KnowledgeDoc>, event?: HistoryEvent) => {
  docs = docs.map((d) => (d.id === id ? { ...d, ...patch, updated: "just now", history: event ? [...d.history, event] : d.history } : d));
  emit();
};

export const approveDoc = (id: string, by: string) => update(id, { status: "live", adminComment: undefined }, { at: "just now", by, text: "Approved" });
export const requestChanges = (id: string, by: string, comment: string) =>
  update(id, { status: "changes_requested", adminComment: comment }, { at: "just now", by, text: `Requested changes — ${comment}` });
export const rejectDoc = (id: string, by: string, reason: string) => update(id, { status: "rejected", adminComment: reason }, { at: "just now", by, text: `Rejected — ${reason}` });

export interface NewDocInput {
  name: string;
  size: string;
  product: string;
  type: string;
  visibleTo: Audience[];
  owner: string;
}

// Admin uploads publish straight to live; everyone else's go to Approvals.
export const addDocs = (items: NewDocInput[], by: string, publish: boolean, note?: string) => {
  const created: KnowledgeDoc[] = items.map((it, i) => ({
    id: `new_${Date.now()}_${i}`,
    name: it.name,
    product: it.product,
    type: it.type,
    visibleTo: it.visibleTo,
    uploadedBy: it.owner || by,
    size: it.size,
    status: publish ? "live" : "pending",
    updated: "just now",
    note,
    preview: PREVIEW,
    mine: true,
    history: [{ at: "just now", by, text: publish ? "Published to knowledge base" : "Submitted for approval" }],
  }));
  docs = [...created, ...docs];
  emit();
};

// Re-submitting a document after "Changes requested".
export const resubmitDoc = (id: string, by: string, fileName: string) =>
  update(id, { status: "pending", name: fileName, adminComment: undefined }, { at: "just now", by, text: "Uploaded a revised file" });

export const pendingCount = (list: KnowledgeDoc[]) => list.filter((d) => d.status === "pending" || d.status === "changes_requested").length;

// CRM Admins approve; agents submit. Org owner (business_admin) and the
// admin/crm_admin org roles count as approvers — see utils/org-role.ts.
export const isSupportBotApprover = (): boolean => {
  const role = localStorage.getItem("role");
  const orgRole = getStoredOrgRole();
  return role === "business_admin" || orgRole === "admin" || orgRole === "crm_admin";
};

// Which Support Agent screens a user gets: the org owner and org_role
// "admin" see all of them, crm_admin only Setup (for now), and everyone
// else (conversation_agent) everything but Setup.
export const canSeeSupportAgentScreen = (screen: string): boolean => {
  const role = localStorage.getItem("role");
  const orgRole = getStoredOrgRole();
  if (role === "business_admin" || orgRole === "admin") return true;
  if (orgRole === "crm_admin") return screen === "Setup";
  return screen !== "Setup";
};

export const currentUserLabel = (): string => {
  try {
    const u = JSON.parse(localStorage.getItem("userDetails") || "null");
    const name = u?.first_name || u?.name;
    if (name) return `${name} (${isSupportBotApprover() ? "CRM Admin" : "Agent"})`;
  } catch {
    /* fall through */
  }
  return isSupportBotApprover() ? "Siddhi (CRM Admin)" : "Priya (Agent)";
};
